import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, Alert, Snackbar,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  CircularProgress, Chip, Checkbox, Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Sms as SmsIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  NotificationsActive as NotificationsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../contexts/UserContext';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const CommunicationSettingsPage = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    if (user) loadSettings();
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await fetchJson('/api/school-setup-and-configuration/communication-settings');
      setSettings(data);
    } catch (err) {
      showSnackbar(t('communicationSettingsPage.errors.load'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetchJson('/api/school-setup-and-configuration/communication-settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      showSnackbar(t('communicationSettingsPage.snack.saved'));
    } catch (err) {
      showSnackbar(t('communicationSettingsPage.errors.save'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await fetchJson('/api/school-setup-and-configuration/communication-settings/test-email', { method: 'POST' });
      showSnackbar(res?.message || t('communicationSettingsPage.snack.testEmailSent'));
    } catch (err) {
      showSnackbar(err?.message || t('communicationSettingsPage.errors.testEmailFailed'), 'error');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const res = await fetchJson('/api/school-setup-and-configuration/communication-settings/test-sms', { method: 'POST' });
      showSnackbar(res?.message || t('communicationSettingsPage.snack.testSmsSent'));
    } catch (err) {
      showSnackbar(err?.message || t('communicationSettingsPage.errors.testSmsFailed'), 'error');
    } finally {
      setTestingSms(false);
    }
  };

  if (!user) return <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />;
  if (loading && !settings) return <AppLoader variant="circular3" size={60} centered minHeight="60vh" messages={[]} />;

  const emailConfigured = !!(settings?.email?.smtpServer && settings?.email?.username);
  const smsConfigured = !!(settings?.sms?.provider && settings?.sms?.apiKey && settings?.sms?.enabled);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1">
          <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('communicationSettingsPage.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('communicationSettingsPage.buttons.saving') : t('communicationSettingsPage.buttons.saveSettings')}
        </Button>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <strong>{t('communicationSettingsPage.info.title')}</strong> {t('communicationSettingsPage.info.body')}
      </Alert>

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={emailConfigured ? <CheckCircleIcon /> : <EmailIcon />}
          label={emailConfigured ? t('communicationSettingsPage.status.schoolEmailConfigured') : t('communicationSettingsPage.status.systemDefaultEmail')}
          color={emailConfigured ? 'success' : 'default'}
          variant={emailConfigured ? 'filled' : 'outlined'}
        />
        <Chip
          icon={smsConfigured ? <CheckCircleIcon /> : <SmsIcon />}
          label={smsConfigured ? t('communicationSettingsPage.status.smsEnabled') : t('communicationSettingsPage.status.smsNotConfigured')}
          color={smsConfigured ? 'success' : 'default'}
          variant={smsConfigured ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Email Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            {t('communicationSettingsPage.email.title')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={testingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
            onClick={handleTestEmail}
            disabled={testingEmail || !emailConfigured}
          >
            {testingEmail ? t('communicationSettingsPage.buttons.sending') : t('communicationSettingsPage.buttons.sendTestEmail')}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('communicationSettingsPage.email.description')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.email.fields.smtpServer')}
              value={settings?.email?.smtpServer || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                email: { ...(prev?.email || {}), smtpServer: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.email.fields.smtpServerPlaceholder')}
              helperText={t('communicationSettingsPage.email.fields.smtpServerHelper')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.email.fields.smtpPort')}
              value={settings?.email?.smtpPort || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                email: { ...(prev?.email || {}), smtpPort: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.email.fields.smtpPortPlaceholder')}
              helperText={t('communicationSettingsPage.email.fields.smtpPortHelper')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.email.fields.fromName', { defaultValue: 'Sender Name (From Name)' })}
              value={settings?.email?.fromName || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                email: { ...(prev?.email || {}), fromName: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.email.fields.fromNamePlaceholder', { defaultValue: 'School / App name' })}
              helperText={t('communicationSettingsPage.email.fields.fromNameHelper', { defaultValue: 'Display name shown to recipients (not the email address)' })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.email.fields.username')}
              value={settings?.email?.username || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                email: { ...(prev?.email || {}), username: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.email.fields.usernamePlaceholder')}
              helperText={t('communicationSettingsPage.email.fields.usernameHelper')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.email.fields.password')}
              type="password"
              value={settings?.email?.password || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                email: { ...(prev?.email || {}), password: e.target.value }
              }))}
              helperText={t('communicationSettingsPage.email.fields.passwordHelper')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.email?.useSSL || false}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...(prev?.email || {}), useSSL: e.target.checked }
                  }))}
                />
              }
              label={t('communicationSettingsPage.email.fields.useSsl')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.email?.requireAuth !== false}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...(prev?.email || {}), requireAuth: e.target.checked }
                  }))}
                />
              }
              label={t('communicationSettingsPage.email.fields.requireAuth')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SMS Configuration */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SmsIcon sx={{ mr: 1 }} />
            {t('communicationSettingsPage.sms.title')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={testingSms ? <CircularProgress size={16} /> : <SmsIcon />}
            onClick={handleTestSms}
            disabled={testingSms || !smsConfigured}
          >
            {testingSms ? t('communicationSettingsPage.buttons.sending') : t('communicationSettingsPage.buttons.sendTestSms')}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('communicationSettingsPage.sms.description')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('communicationSettingsPage.sms.fields.provider')}</InputLabel>
              <Select
                value={settings?.sms?.provider || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  sms: { ...(prev?.sms || {}), provider: e.target.value }
                }))}
                label={t('communicationSettingsPage.sms.fields.provider')}
              >
                <MenuItem value="">{t('communicationSettingsPage.sms.fields.providerPlaceholder')}</MenuItem>
                <MenuItem value="twilio">{t('communicationSettingsPage.sms.providers.twilio')}</MenuItem>
                <MenuItem value="africastalking">{t('communicationSettingsPage.sms.providers.africastalking')}</MenuItem>
                <MenuItem value="nexmo">{t('communicationSettingsPage.sms.providers.nexmo')}</MenuItem>
                <MenuItem value="custom">{t('communicationSettingsPage.sms.providers.custom')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.sms.fields.apiKey')}
              value={settings?.sms?.apiKey || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                sms: { ...(prev?.sms || {}), apiKey: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.sms.fields.apiKeyPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.sms.fields.apiSecret')}
              type="password"
              value={settings?.sms?.apiSecret || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                sms: { ...(prev?.sms || {}), apiSecret: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.sms.fields.apiSecretPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('communicationSettingsPage.sms.fields.senderId')}
              value={settings?.sms?.senderId || ''}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                sms: { ...(prev?.sms || {}), senderId: e.target.value }
              }))}
              placeholder={t('communicationSettingsPage.sms.fields.senderIdPlaceholder')}
              helperText={t('communicationSettingsPage.sms.fields.senderIdHelper')}
            />
          </Grid>
          {settings?.sms?.provider === 'custom' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('communicationSettingsPage.sms.fields.gatewayUrl')}
                value={settings?.sms?.gatewayUrl || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  sms: { ...(prev?.sms || {}), gatewayUrl: e.target.value }
                }))}
                placeholder={t('communicationSettingsPage.sms.fields.gatewayUrlPlaceholder')}
                helperText={t('communicationSettingsPage.sms.fields.gatewayUrlHelper')}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.sms?.enabled || false}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    sms: { ...(prev?.sms || {}), enabled: e.target.checked }
                  }))}
                />
              }
              label={t('communicationSettingsPage.sms.fields.enable')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Notification Preferences */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <NotificationsIcon sx={{ mr: 1 }} />
          {t('communicationSettingsPage.notificationPreferences.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('communicationSettingsPage.notificationPreferences.description')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1}>
          {[
            { key: 'credentialsNotification' },
            { key: 'feePaymentNotification' },
            { key: 'feeReminderNotification' },
            { key: 'absenceNotification' },
            { key: 'announcementNotification' },
            { key: 'reportCardNotification' },
            { key: 'disciplineNotification' },
            { key: 'eventReminderNotification' }
          ].map(({ key }) => (
            <Grid item xs={12} sm={6} key={key}>
              <Box
                sx={{
                  display: 'flex', alignItems: 'flex-start', p: 1.5, borderRadius: 1,
                  border: '1px solid', borderColor: settings?.notificationPreferences?.[key] ? 'primary.light' : 'divider',
                  bgcolor: settings?.notificationPreferences?.[key] ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s', cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                }}
                onClick={() => setSettings(prev => ({
                  ...prev,
                  notificationPreferences: {
                    ...(prev?.notificationPreferences || {}),
                    [key]: !prev?.notificationPreferences?.[key]
                  }
                }))}
              >
                <Checkbox
                  checked={!!settings?.notificationPreferences?.[key]}
                  color="primary"
                  sx={{ p: 0, mr: 1.5, mt: 0.2 }}
                  onChange={() => {}}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">{t(`communicationSettingsPage.notificationPreferences.items.${key}.label`)}</Typography>
                  <Typography variant="caption" color="text.secondary">{t(`communicationSettingsPage.notificationPreferences.items.${key}.desc`)}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>{t('communicationSettingsPage.notificationPreferences.noteTitle')}</strong> {t('communicationSettingsPage.notificationPreferences.noteBody')}
        </Alert>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationSettingsPage;
